import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";

import { FiUser, FiLock } from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { IoMdEyeOff } from "react-icons/io";

import { AuthContext } from "../Auth/AuthProvider";
import LoadingScreen from "../Componentes/LoadingScreen";

// Imagens do carousel. Adicione novas entradas aqui conforme necessário.
const CAROUSEL_IMAGES = [
    "/Images/PREPARO_DE_MADEIRA.jpg"
];

const CAROUSEL_INTERVAL_MS = 5000;

/**
 * Paleta alinhada ao módulo de Atendimento Ambulatorial:
 * - Brand dark: #263B4B (NavBar / GenericBody)
 * - Acento azul (foco): #2563eb / #4a90e2
 * - Primário verde: #4caf50 / #45a049
 * - Bordas suaves: rgba(0,0,0,0.15) / rgba(0,0,0,0.3)
 * - Tipografia: Segoe UI
 */

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
    width: 100%;
    height: 100dvh;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    background-color: #263b4b;
    background:
        linear-gradient(170deg, rgba(37, 99, 235, 0.22), transparent 55%),
        #263b4b;
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
`;

const FormSide = styled.div`
    width: 35%;
    max-width: 480px;
    min-width: 360px;
    flex-shrink: 0;
    height: 100%;
    box-sizing: border-box;
    padding: 48px 44px;
    color: #fff;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    animation: ${fadeIn} 0.4s ease both;

    @media (max-width: 768px) {
        width: 100%;
        max-width: none;
        min-width: 0;
        padding: 36px 28px;
    }
`;

const ImageSide = styled.div`
    flex: 1;
    min-width: 0;
    height: 100%;
    box-sizing: border-box;
    padding: 15px;

    @media (max-width: 768px) {
        display: none;
    }
`;

const ImageFrame = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    overflow: hidden;
`;

const Slide = styled.div`
    position: absolute;
    inset: 0;
    background-image: url(${(p) => p.$src});
    background-size: cover;
    background-position: center;
    opacity: ${(p) => (p.$active ? 1 : 0)};
    transition: opacity 0.8s ease;
`;

const Dots = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 18px;
    display: flex;
    justify-content: center;
    gap: 8px;
    z-index: 2;
`;

const Dot = styled.button`
    all: unset;
    width: ${(p) => (p.$active ? "24px" : "8px")};
    height: 8px;
    border-radius: 999px;
    cursor: pointer;
    background-color: ${(p) =>
        p.$active ? "#fff" : "rgba(255, 255, 255, 0.5)"};
    transition: width 0.3s ease, background-color 0.3s ease;
`;

const BrandLogo = styled.img`
    height: 42px;
    align-self: flex-start;
`;

const FormBlock = styled.div`
    display: flex;
    flex-direction: column;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Field = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FieldLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    letter-spacing: 0.02em;
`;

const InputWrapper = styled.div`
    display: flex;
    align-items: center;
    height: 46px;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
        border-color: #4a90e2;
        box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.25);
    }
`;

const IconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 100%;
    color: rgba(0, 0, 0, 0.45);

    svg {
        width: 18px;
        height: 18px;
    }
`;

const Input = styled.input`
    flex: 1;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    box-sizing: border-box;
    padding-right: 12px;
    font-size: 14px;
    color: #1f2937;
    font-family: inherit;

    &::placeholder {
        color: #9ca3af;
    }
`;

const ShowPass = styled.button`
    all: unset;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 100%;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.45);
    transition: color 0.15s ease;

    &:hover {
        color: #2563eb;
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

const PrimaryButton = styled.button`
    height: 46px;
    margin-top: 12px;
    border: none;
    border-radius: 10px;
    background-color: #4caf50;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.15s ease;

    &:hover:not(:disabled) {
        background-color: #45a049;
    }

    &:active:not(:disabled) {
        transform: scale(0.99);
    }

    &:disabled {
        background-color: #c1c1c1;
        cursor: not-allowed;
    }
`;

const SecondaryButton = styled.button`
    height: 46px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 10px;
    background-color: transparent;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease;

    &:hover {
        border-color: #fff;
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

const ErrorMessage = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: #ffb4b4;
    min-height: 16px;
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 18px 0 6px;
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
    letter-spacing: 0.06em;

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background-color: rgba(255, 255, 255, 0.18);
    }
`;

const Footer = styled.span`
    font-size: 11px;
    letter-spacing: 0.04em;
    color: rgba(255, 255, 255, 0.5);
`;

const LoginV2 = () => {
    const navigate = useNavigate();
    const { loading, login } = useContext(AuthContext);

    const [passView, setPassView] = useState(false);
    const [usuario, setUsuario] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [slide, setSlide] = useState(0);

    useEffect(() => {
        if (CAROUSEL_IMAGES.length <= 1) return undefined;

        const timer = setInterval(() => {
            setSlide((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
        }, CAROUSEL_INTERVAL_MS);

        return () => clearInterval(timer);
    }, []);

    const showPass = () => setPassView((prev) => !prev);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (submitting) return;

        setError("");
        setSubmitting(true);

        try {
            await login(usuario, password);
            navigate("/", { replace: true });
        } catch (err) {
            console.error(err);
            setError("Usuário ou senha inválidos. Tente novamente.");
        } finally {
            setSubmitting(false);
        }
    };

    const navigateRegister = () => navigate("/register");

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <Page>
            <FormSide>
                <BrandLogo src="/Images/Logo.png" alt="Palmont" />

                <FormBlock>
                    <Form onSubmit={handleSubmit}>
                        <Field>
                            <FieldLabel>USUÁRIO</FieldLabel>
                            <InputWrapper>
                                <IconBox>
                                    <FiUser />
                                </IconBox>
                                <Input
                                    type="text"
                                    placeholder="Seu usuário"
                                    value={usuario}
                                    onChange={(e) => setUsuario(e.target.value)}
                                    autoComplete="username"
                                    required
                                />
                            </InputWrapper>
                        </Field>

                        <Field>
                            <FieldLabel>SENHA</FieldLabel>
                            <InputWrapper>
                                <IconBox>
                                    <FiLock />
                                </IconBox>
                                <Input
                                    type={passView ? "text" : "password"}
                                    placeholder="Sua senha"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                />
                                <ShowPass
                                    type="button"
                                    onClick={showPass}
                                    aria-label={
                                        passView
                                            ? "Ocultar senha"
                                            : "Mostrar senha"
                                    }
                                >
                                    {passView ? <IoEye /> : <IoMdEyeOff />}
                                </ShowPass>
                            </InputWrapper>
                        </Field>

                        <ErrorMessage>{error}</ErrorMessage>

                        <PrimaryButton type="submit" disabled={submitting}>
                            {submitting ? "ENTRANDO..." : "ENTRAR"}
                        </PrimaryButton>

                        <Divider>OU</Divider>

                        <SecondaryButton
                            type="button"
                            onClick={navigateRegister}
                        >
                            CRIAR CONTA
                        </SecondaryButton>
                    </Form>
                </FormBlock>

                <Footer>P-SIG · V.2026.1.4.0</Footer>
            </FormSide>

            <ImageSide>
                <ImageFrame>
                    {CAROUSEL_IMAGES.map((src, index) => (
                        <Slide
                            key={index}
                            $src={src}
                            $active={index === slide}
                        />
                    ))}
                    {CAROUSEL_IMAGES.length > 1 && (
                        <Dots>
                            {CAROUSEL_IMAGES.map((_, index) => (
                                <Dot
                                    key={index}
                                    type="button"
                                    $active={index === slide}
                                    onClick={() => setSlide(index)}
                                    aria-label={`Ir para imagem ${index + 1}`}
                                />
                            ))}
                        </Dots>
                    )}
                </ImageFrame>
            </ImageSide>
        </Page>
    );
};

export default LoginV2;
